import React, { useState, useEffect } from "react";
import classNames from "classnames";
import { withRouter } from "react-router-dom";
import { intersection, uniq } from "lodash";
import { RouteComponentProps } from "react-router";
import { Theme, withStyles } from "@material-ui/core/styles";
import createStyles from "@material-ui/core/styles/createStyles";
import withContext from "../../../tools/withContext";
import BatchDeadlineFormDialog from "./BatchDeadlineFormDialog";
import BatchAttribFormDialog from "./BatchAttribFormDialog";
import ApplyLabelingDialog from "./ApplyLabelingDialog";
import AlertDialog from "../../common/AlertDialog";
import BatchType from "../../../types/Batch";
import UiButton from "../../common/UiButton";
import UiArrowIcon from "../../common/UiArrowIcon";
import ContextType from "../../../types/Context";
import UserType from "../../../types/User";
import BatchActionsMenu, {
  getVisibleBatchMenuItems,
} from "./BatchActionsMenu";
import generateType from "./generateType";
import { BATCH_STATUS } from "./FilteringBatchesDialog";
import { tableRowActionButtonModernStyle } from "../../common/tableTheme";

const styles = (theme: Theme) =>
  createStyles({
    actionButtonModern: tableRowActionButtonModernStyle(theme),
  });

interface PropsType extends RouteComponentProps<any>, React.Props<any> {
  batch: BatchType;
  users: UserType[];
  batches: BatchType[];
  selected: any[];
  history: any;
  saveBatch: Function;
  massUpdate: Function;
  selectOne: Function; // Quand le bouton Action est cliqué on ne sélectionne que le batch lié
  deleteBatch: Function;
  deleteDeadline: Function;
  applyLabeling: Function;
  pushToHistory: Function;
  setModalMode: Function;
  isAdmin: boolean;
  ctx: ContextType;
  theme: any;
  classes: any;
  variant?: "default" | "modern";
}

const BatchActionButton = (props: PropsType) => {
  const [anchorEl, setAnchorEl] = useState<any>(null);
  const [alertMessage, setAlertMessage] = useState<any>(null);
  const [batchDialogOpen, setBatchDialogOpen] = useState(false);
  const [formView, setFormView] = useState<"attrib" | "deadline" | "applyLabeling">("attrib");
  const [selectedBatchIds, setSelectedBatchIds] = useState<number[]>([]);
  const [usersForSelection, setUsersForSelection] = useState<UserType[]>([]);

  const {
    batch,
    users,
    batches,
    selected,    
    history,
    saveBatch,
    massUpdate,
    deleteBatch,
    deleteDeadline,
    applyLabeling,
    pushToHistory,
    isAdmin,
    selectOne,
    setModalMode,
    classes,
    variant = "default",
    ctx: {
      user: {
        authorities: [userRole],
        token,
      },
    },
  } = props;
  const isModern = variant === "modern";

  const hasMenuItems =
    getVisibleBatchMenuItems(batch, userRole, {
      closeMenu: () => {},
      openDialog: () => {},
      openAlert: () => {},
      pushToHistory: () => {},
      history,
      token: token ?? "",
    }).length > 0;

  const actionsDisabled = selectedBatchIds.length > 1 || !hasMenuItems;

  useEffect(
    () => setModalMode(batchDialogOpen || alertMessage),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [batchDialogOpen, alertMessage]
  );

  useEffect(() => {
    setSelectedBatchIds(selected);
  }, [selected]);

  useEffect(() => {
    const batchesSelected = batches.filter(
      (b: BatchType) => selectedBatchIds.indexOf(b.id) !== -1
    );
    const batchesSelectedProjectsIds = uniq(
      batchesSelected.reduce((m: any, b: any) => m.concat(b.project.id), [])
    );
    // récupérer les users rattachés à tous les projets batchesSelectedProjectsIds
    setUsersForSelection(
      users.filter((u: any) => {
        const i = intersection(u.projects, batchesSelectedProjectsIds);
        return (
          (i.length > 0 && i.length === batchesSelectedProjectsIds.length) ||
          u.authorities.find((a: string) => a === "ROLE_ADMIN")
        );
      })
    );
  }, [users, batches, selectedBatchIds, formView]);

  const handleLabeler = (batchId: number) => {
    pushToHistory(batchId);
    const type = generateType(batch);
    const lastLabeledImage =
      batch.totalLabelDocs < batch.totalEligibleDocs
        ? batch.totalLabelDocs
        : 0;
    window.open(`/labeler${type}/${batchId}/${lastLabeledImage}`, "_blank");
  };

  const actionButtonClicked = (e: any) => {
    if (!hasMenuItems) return;
    e.stopPropagation();
    setSelectedBatchIds([batch.id]);
    setAnchorEl(e.currentTarget);
  };

  const closeMenu = () => setAnchorEl(null);

  const openDialog = (view: "attrib" | "deadline" | "applyLabeling") => {
    setFormView(view);
    setBatchDialogOpen(true);
  };

  const closeDialog = () => setBatchDialogOpen(false);

  const openAlert = (message: any) => setAlertMessage(message);

  const closeAlert = () => setAlertMessage(null);

  const handleSave = (datas: any) => {
    if (datas.labelerId || datas.deadline) {
      saveBatch({ ...batch, ...datas });
    } else if (datas.ids) {
      massUpdate(datas).then(() => closeDialog());
    }
    closeDialog();
  };

  const handleDeleteDeadling = (batch: any) => {
    deleteDeadline(batch);
    closeDialog();
  };

  const handleApplyLabeling = (
    payload: any,
    onProgress: (saved: number, total: number) => void
  ) => applyLabeling(payload, onProgress).then(() => closeDialog());


  const handleAlertActionAccepted = () => {
    if (alertMessage && alertMessage.type === "DELETE") {
      deleteBatch(batch).then(() => closeAlert());
      return;
    }

    if (alertMessage.type in BATCH_STATUS) {
      saveBatch({ ...batch, state: alertMessage.type  }).then(() =>
        closeAlert()
      );
    }
  };  

  return isAdmin || batch.totalLabelDocs === batch.totalEligibleDocs ? (
    <React.Fragment>
      {batchDialogOpen && formView === "deadline" && (
        <BatchDeadlineFormDialog
          open={batchDialogOpen}
          onClose={closeDialog}
          onSave={handleSave}
          batch={batch}
          users={usersForSelection}
          selected={selected}
          deleteDeadline={handleDeleteDeadling}
        />
      )}
      {batchDialogOpen && formView === "attrib" && (
        <BatchAttribFormDialog
          open={batchDialogOpen}
          onClose={closeDialog}
          onSave={handleSave}
          batch={batch}
          users={usersForSelection}
          selected={selected}
          batches={batches.filter((l: BatchType) => !l.labelerId)}
        />
      )}
      {batchDialogOpen && formView === "applyLabeling" && (
        <ApplyLabelingDialog
          open={batchDialogOpen}
          onClose={closeDialog}
          onApply={handleApplyLabeling}
          batch={batch}
        />
      )}
      {alertMessage !== null && (
        <AlertDialog
          title={alertMessage.title}
          text={alertMessage.text}
          open={alertMessage !== null}
          onClose={closeAlert}
          onAccept={handleAlertActionAccepted}
        />
      )}
      <UiButton
        variant="outlined"
        color="secondary"
        className={classNames({
          [classes.actionButtonModern]: isModern,
        })}
        onClick={(e: any) => actionButtonClicked(e)}
        disabled={actionsDisabled}
        style={
          !isModern && actionsDisabled
            ? { paddingRight: 24, paddingLeft: 24 }
            : undefined
        }
      >
        Actions
        {!actionsDisabled && (
          <UiArrowIcon disabled={selectedBatchIds.length > 1} />
        )}
      </UiButton>
      {anchorEl && hasMenuItems && (
        <BatchActionsMenu
          anchorEl={anchorEl}
          userRole={userRole}
          closeMenu={closeMenu}
          pushToHistory={pushToHistory}
          openDialog={openDialog}
          openAlert={openAlert}
          batch={batch}
        />
      )}
    </React.Fragment>
  ) : (
    <UiButton
      variant="outlined"
      color="secondary"
      className={classNames({
        [classes.actionButtonModern]: isModern,
      })}
      onClick={() => handleLabeler(batch.id)}
      disabled={selectedBatchIds.length > 1}
      style={isModern ? { paddingRight: 24 } : undefined}
    >
      Labelliser
    </UiButton>
  );
};

export default withStyles(styles)(
  withRouter(withContext(BatchActionButton)),
);
